import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  ShieldCheck, 
  Truck, 
  Plus, 
  Minus, 
  Check,
  MessageCircle,
  ShoppingCart
} from 'lucide-react';
import axios from 'axios';
import ShareButtons from '../components/ShareButtons';
import { Layout } from '../components/Layout';
import { CurrencyDisplay } from '../components/CurrencyDisplay';
import { WhatsAppLink } from '../components/WhatsAppButton';
import { useLanguage } from '../contexts/LanguageContext';
import { useQuote } from '../contexts/QuoteContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { getImageUrl, getAllImageUrls } from '../utils/imageUtils';
import SEOHead from '../components/SEOHead';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ProductDetailPage() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const { addToQuote, isInQuote, removeFromQuote, addToCart, isInCart } = useQuote();
  
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/products/${productId}`);
        setProduct(response.data);
        if (response.data.min_quantity) {
          setQuantity(response.data.min_quantity);
        }
      } catch (err) {
        console.error('Product fetch error:', err);
        navigate('/products');
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [productId, navigate]);

  // Redirect old prod_xxx URLs to slug
  useEffect(() => {
    if (product && product.slug && productId.startsWith('prod_') && productId !== product.slug) {
      navigate(`/products/${product.slug}`, { replace: true });
    }
  }, [product, productId, navigate]);

  // Product schema for SEO
  const productSlug = product?.slug || product?.product_id || productId;
  const productSchema = product ? [
    {
      "@context": "https://schema.org",
      "@type": "Product",
      "name": product.name_en,
      "description": product.description_en || `Buy ${product.name_en} from South Africa.`,
      "image": product.images?.[0],
      "sku": product.product_id,
      "url": `https://theguyinsa.co.za/products/${productSlug}`,
      "brand": {"@type": "Brand", "name": product.supplier_name || "The Guy in SA"},
      "offers": {
        "@type": "Offer",
        "priceCurrency": "ZAR",
        "price": product.price_zar,
        "availability": "https://schema.org/InStock",
        "seller": {"@type": "Organization", "name": "The Guy in SA"},
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {"@type": "ListItem", "position": 1, "name": "Home", "item": "https://theguyinsa.co.za"},
        {"@type": "ListItem", "position": 2, "name": "Products", "item": "https://theguyinsa.co.za/products"},
        {"@type": "ListItem", "position": 3, "name": product.name_en, "item": `https://theguyinsa.co.za/products/${productSlug}`},
      ],
    }
  ] : null;

  if (loading) {
    return (
      <Layout>
        <div className="container-custom py-8">
          <div className="animate-pulse">
            <div className="h-8 w-32 bg-slate-200 rounded mb-8" />
            <div className="grid lg:grid-cols-2 gap-12">
              <div className="aspect-square bg-slate-200 rounded-xl" />
              <div className="space-y-4">
                <div className="h-8 w-3/4 bg-slate-200 rounded" />
                <div className="h-6 w-1/2 bg-slate-200 rounded" />
                <div className="h-20 w-full bg-slate-200 rounded" />
              </div>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (!product) return null;

  const name = language === 'pt' ? product.name_pt : product.name_en;
  const description = language === 'pt' ? product.description_pt : product.description_en;
  const inQuote = isInQuote(product.product_id);
  const inCart = isInCart(product.product_id);
  const canBuyNow = !product.is_quote_only;

  const handleQuantityChange = (delta) => {
    const newQty = quantity + delta;
    if (newQty >= (product.min_quantity || 1)) {
      setQuantity(newQty);
    }
  };

  const handleAddToQuote = () => {
    if (inQuote) {
      removeFromQuote(product.product_id);
    } else {
      addToQuote(product, quantity);
    }
  };

  const whatsappMessage = `Hi! I'm interested in: ${name} (Qty: ${quantity}). Can you provide more information?`;

  return (
    <Layout>
      {product && (
        <SEOHead
          title={`${product.name_en} | Buy from South Africa | The Guy in SA`}
          description={(product.description_en || `Buy ${product.name_en} from South Africa. Delivered to Mozambique.`).slice(0, 160)}
          image={product.images?.[0]}
          schema={productSchema}
        />
      )}
      <div className="container-custom py-8" data-testid="product-detail-page">
        {/* Back Button */}
        <Link 
          to="/products"
          className="inline-flex items-center gap-2 text-slate-600 hover:text-[#1E3A8A] mb-8 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          {t('common_back')}
        </Link>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Images */}
          <div className="space-y-4">
            <div className="aspect-square rounded-xl overflow-hidden bg-slate-100">
              {getAllImageUrls(product.images)[selectedImage] ? (
                <img
                  src={getAllImageUrls(product.images)[selectedImage]}
                  alt={name}
                  loading="eager"
                  decoding="sync"
                  fetchpriority="high"
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-slate-400">
                  No image available
                </div>
              )}
            </div>
            
            {getAllImageUrls(product.images).length > 1 && (
              <div className="flex gap-2 overflow-x-auto pb-2">
                {getAllImageUrls(product.images, true).map((imageUrl, index) => (
                  <button
                    key={imageUrl}
                    onClick={() => setSelectedImage(index)}
                    className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-colors ${
                      selectedImage === index 
                        ? 'border-[#1E3A8A]' 
                        : 'border-transparent hover:border-slate-300'
                    }`}
                  >
                    <img
                      src={imageUrl}
                      alt={`${name} ${index + 1}`}
                      loading="lazy"
                      decoding="async"
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product Info */}
          <div className="space-y-6">
            {/* Badges */}
            <div className="flex flex-wrap gap-2">
              {product.is_approved && (
                <Badge className="bg-[#10B981]/10 text-[#10B981] hover:bg-[#10B981]/20">
                  <ShieldCheck className="w-3 h-3 mr-1" />
                  Verified Product
                </Badge>
              )}
              {product.is_quote_only && (
                <Badge variant="outline">
                  {t('products_quote_only')}
                </Badge>
              )}
            </div>

            {/* Title */}
            <h1 className="text-3xl font-heading font-bold text-slate-900">
              {name}
            </h1>

            {/* Supplier */}
            {product.supplier_name && (
              <p className="text-slate-600">
                Supplied by <span className="font-medium text-slate-900">{product.supplier_name}</span>
              </p>
            )}

            {/* Share */}
            <ShareButtons
              url={`https://theguyinsa.co.za/products/${product.slug || product.product_id}`}
              title={`${name} - The Guy in SA`}
              description={product.description_en?.slice(0, 120)}
            />

            {/* Price */}
            <div className="p-6 bg-slate-50 rounded-xl">
              {!product.is_quote_only ? (
                <>
                  <CurrencyDisplay amountZAR={product.price_zar} size="xl" />
                  <p className="text-sm text-slate-500 mt-2">
                    per {product.unit || 'unit'}
                  </p>
                </>
              ) : (
                <p className="text-lg text-[#F59E0B] font-semibold">
                  Contact us for pricing
                </p>
              )}
            </div>

            {/* Quantity */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Quantity
              </label>
              <div className="flex items-center gap-4">
                <div className="flex items-center border border-slate-300 rounded-lg">
                  <button
                    onClick={() => handleQuantityChange(-1)}
                    disabled={quantity <= (product.min_quantity || 1)}
                    className="p-3 hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed rounded-l-lg transition-colors"
                    data-testid="quantity-minus"
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                  <span className="px-6 py-3 font-mono font-medium min-w-[60px] text-center">
                    {quantity}
                  </span>
                  <button
                    onClick={() => handleQuantityChange(1)}
                    className="p-3 hover:bg-slate-100 rounded-r-lg transition-colors"
                    data-testid="quantity-plus"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
                {product.min_quantity > 1 && (
                  <span className="text-sm text-slate-500">
                    Min. order: {product.min_quantity} {product.unit || 'units'}
                  </span>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-col gap-3">
              {canBuyNow ? (
                <>
                  {/* Buy Now */}
                  <Button
                    onClick={() => {
                      addToCart(product, quantity);
                      navigate('/cart');
                    }}
                    className="w-full h-14 bg-[#F59E0B] hover:bg-[#F59E0B]/90 text-[#0F172A] font-bold text-base btn-shine"
                    data-testid="buy-now-btn"
                  >
                    <ShoppingCart className="w-5 h-5 mr-2" />
                    {language === 'pt' ? 'Comprar Agora' : 'Buy Now'}
                  </Button>
                  {/* Add to Cart */}
                  <div className="flex gap-3">
                    <Button
                      onClick={() => addToCart(product, quantity)}
                      variant={inCart ? "default" : "outline"}
                      className={`flex-1 h-12 ${inCart ? 'bg-[#10B981] hover:bg-[#10B981]/90 text-white' : ''}`}
                      data-testid="add-to-cart-btn"
                    >
                      {inCart ? (
                        <><Check className="w-5 h-5 mr-2" />{language === 'pt' ? 'No Carrinho' : 'In Cart'}</>
                      ) : (
                        <><Plus className="w-5 h-5 mr-2" />{language === 'pt' ? 'Adicionar ao Carrinho' : 'Add to Cart'}</>
                      )}
                    </Button>
                    <WhatsAppLink message={whatsappMessage}>
                      <Button variant="outline" className="h-12 px-6">
                        <MessageCircle className="w-5 h-5 mr-2" />
                        WhatsApp
                      </Button>
                    </WhatsAppLink>
                  </div>
                </>
              ) : (
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button
                    onClick={handleAddToQuote}
                    className={`flex-1 h-12 ${
                      inQuote ? 'bg-[#10B981] hover:bg-[#10B981]/90' : 'bg-[#1E3A8A] hover:bg-[#1E3A8A]/90'
                    }`}
                    data-testid="add-to-quote-btn"
                  >
                    {inQuote ? (
                      <><Check className="w-5 h-5 mr-2" />{language === 'pt' ? 'Na Cotacao' : 'Added to Quote'}</>
                    ) : (
                      <><Plus className="w-5 h-5 mr-2" />{t('products_add_quote')}</>
                    )}
                  </Button>
                  <WhatsAppLink message={whatsappMessage}>
                    <Button variant="outline" className="h-12 px-6">
                      <MessageCircle className="w-5 h-5 mr-2" />
                      WhatsApp
                    </Button>
                  </WhatsAppLink>
                </div>
              )}
            </div>

            {/* Shipping Info */}
            <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg">
              <Truck className="w-5 h-5 text-[#1E3A8A] mt-0.5" />
              <div>
                <p className="font-medium text-slate-900">Delivery to Mozambique</p>
                <p className="text-sm text-slate-600">
                  We handle collection, customs, and delivery to your door.
                </p>
              </div>
            </div>

            {/* Description */}
            {description && (
              <div>
                <h3 className="text-lg font-heading font-semibold text-slate-900 mb-3">
                  Description
                </h3>
                <p className="text-slate-600 whitespace-pre-line">
                  {description}
                </p>
              </div>
            )}

            {/* Specifications */}
            {product.specifications && Object.keys(product.specifications).length > 0 && (
              <div>
                <h3 className="text-lg font-heading font-semibold text-slate-900 mb-3">
                  Specifications
                </h3>
                <dl className="grid grid-cols-2 gap-4">
                  {Object.entries(product.specifications).map(([key, value]) => (
                    <div key={key} className="p-3 bg-slate-50 rounded-lg">
                      <dt className="text-sm text-slate-500">{key}</dt>
                      <dd className="font-medium text-slate-900">{value}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
